import { css, nothing } from 'lit'
import { html, unsafeStatic } from 'lit/static-html.js'
import HHQuizElement from '../element.js'
import store from '../store/index.js'

/**
 * Quiz modal
 * @extends HHQuizElement
 */

class HHQuizModal extends HHQuizElement {
  static properties = {
    size: { type: String },
    id: { type: String },
    title: { type: String },
  }

  constructor() {
    super()
    this.size = 'lg'
  }

  /**
   * Adds 'ESC' key listener
   */
  connectedCallback() {
    super.connectedCallback()
    document.addEventListener('keydown', this.handleKeydown)
  }

  /**
   * Removes 'ESC' key listener
   */
  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown)
    super.disconnectedCallback()
  }

  // Handle 'ESC' key
  handleKeydown(e) {
    if ((e || window.event).keyCode == 27) store.closeModal()
  }

  /**
   * Focuses the first focusable element
   */
  updated() {
    if (!this.isActive) return;
    this.dispatchEvent(new CustomEvent('modal-opened', { bubbles: true, composed: true }))
    this.focusFirstWithin(['.focus-first', '.hh-quiz-modal__close'])
  }

  get isOpen() {
    return this.store.currentModal === this.id
  }

  get isActive() {
    return this.isOpen && this.store.modalBgVisible
  }

  render() {
    return this.isOpen
      ? html`<hh-focus-trap active="${this.isActive}"
          ><div class="hh-quiz-modal"
            @click="${() => this.store.closeModal()}"
            >
            <div
              class="hh-quiz-modal__el ${this.size} ${this.store.modalBgVisible
                ? ''
                : 'not-visible'}"
              @click="${(ev) => ev.stopPropagation()}"
            >
              <div class="hh-quiz-modal__header">
                <div class="h4">${this.title}</div>
                <button
                  class="hh-quiz-modal__close"
                  @keydown="${(ev) => ev.stopPropagation()}"
                  @click="${() => this.store.closeModal()}"
                  aria-label="${HH_QUIZ.t.accessibility.close}"
                >
                  ${unsafeStatic(HH_QUIZ['icons'].close)}
                </button>
              </div>
              <div class="hh-quiz-modal__body">
                <div>
                  <slot></slot>
                </div>
              </div>
            </div></div
        ></hh-focus-trap>`
      : nothing
  }

  static styles = [
    HHQuizElement.styles,
    css`
      .hh-quiz-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: calc(var(--hh-z-highest) * 11);
      }

      .hh-quiz-modal__el {
        background: #fff;
        transition: 0.3s ease;
        max-width: calc(100vw - 60px);
        max-height: calc(100vh - 120px);
        display: flex;
        flex-direction: column;
      }

      .hh-quiz-modal__el.not-visible {
        opacity: 0;
        transform: translateY(-10px);
      }

      .hh-quiz-modal__el.lg {
        width: max(63vw, 914px);
      }

      .hh-quiz-modal__el.md {
        width: max(41vw, 600px);
      }

      .hh-quiz-modal__el.sm {
        width: max(29vw, 427px);
      }

      @media screen and (max-width: ${HHQuizElement.mq.xs}px) {
        .hh-quiz-modal__el {
          max-width: calc(100vw - 40px);
          max-height: calc(100vh - 40px);
        }

        .hh-quiz-modal__el.md,
        .hh-quiz-modal__el.lg {
          min-width: 100%;
          max-width: 100%;
          min-height: 100%;
          max-height: 100%;
        }
      }

      .hh-quiz-modal__header {
        height: 60px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 25px;
        border-bottom: 1px solid var(--hh-color-grey-mineral);
        color: var(--hh-color-aged-black);
      }

      @media screen and (max-width: ${HHQuizElement.mq.xs}px) {
        .hh-quiz-modal__header {
          padding: 0 20px;
        }
      }

      .hh-quiz-modal__header button {
        padding: 0;
        border: none;
        background: none;
        cursor: pointer;
      }

      .hh-quiz-modal__header button:hover {
        opacity: 0.7;
      }

      .hh-quiz-modal__body {
        overflow-y: auto;
        flex-grow: 1;
      }

      .hh-quiz-modal__body > div {
        overflow-y: auto;
        max-height: 100%;
      }

      .hh-quiz-modal__body > div {
        width: 100%;
      }

      .hh-quiz-modal__close svg {
        width: 12px;
      }
    `,
  ]
}

customElements.define('hh-quiz-modal', HHQuizModal)

class HHQuizModalBackground extends HHQuizElement {
  render() {
    return html`<div
      @click="${() => this.store.closeModal()}"
      class="hh-quiz-modal-background ${this.store.modalBgVisible
        ? 'is-visible'
        : ''} ${this.store.currentModal ? 'is-active' : ''}"
    ></div>`
  }

  static styles = [
    HHQuizElement.styles,
    css`
      .hh-quiz-modal-background {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: var(--hh-color-overlay);
        opacity: 0;
        z-index: -1;
        transition: opacity 0.3s ease;
      }
      .hh-quiz-modal-background.is-active {
        z-index: calc(var(--hh-z-highest) * 10);
      }
      .hh-quiz-modal-background.is-visible {
        opacity: 1;
      }
    `,
  ]
}

customElements.define('hh-quiz-modal-background', HHQuizModalBackground)
